import { Schema } from "@generation/core";
import { IPageFlowStep } from "@shared/shared-generic-rekenmodule";
import { useAtom } from "jotai";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { klantContactResultAtom } from "../atom/KlantContactResultAtom";
import { parsedHtmlElement } from "../form/interface/formContentData";
import { RequestInformation } from "../form/interface/RequestInformation";
import { SchemaToRequestMapper } from "../form/mapper/SchemaToRequestMapper";
import { KlantContactService } from "../form/service/KlantContactService";
import { getSchemas } from "../form/utils/pageflowStepHelper";
import { IHandleSubmitProps } from "../interfaces/IHandleSubmitProps";
import { ISubmitFormulierResult } from "../interfaces/submit-formulier-results";
import { translate } from "../locales/src/translate";
import { DownloadComponent } from "./DownloadComponent";
import { LoadingSpinner } from "./LoadingSpinner";

export interface IResultPageProps {
  values: any;
  apiUrl: string;
  language: string;
  formulierKenmerk: string;
  onderwerp: string;
  pageFlowSteps: IPageFlowStep[];
  thankYouPageContent?: parsedHtmlElement;
  downloadInformationContent?: parsedHtmlElement;
  overzichtPath?: string;
  pageBaseUrl: string;
  onSubmit?: () => Promise<void>;
  requestInformation?: RequestInformation;
  handleSubmit?: (props: IHandleSubmitProps, schemas: Schema[]) => Promise<ISubmitFormulierResult>;
  schemaToRequestMapper?: SchemaToRequestMapper;
}

export const ResultPage = (props: IResultPageProps) => {
  const [klantContactResult, setklantContactResult] = useAtom(klantContactResultAtom);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<Error | null>(null);
  const history = useHistory();
  const location = useLocation();

  let pageBaseUrl = props.pageBaseUrl;
  if (props.pageBaseUrl.startsWith("/")) {
    pageBaseUrl = props.pageBaseUrl.substring(1);
  }

  // Set the previous page to the start of the form.
  React.useEffect(() => {
    history.replace(`/${pageBaseUrl}`);
    history.push(location.pathname);
  }, []);

  React.useEffect(() => {
    const fetchData = async () => {
      let result: ISubmitFormulierResult;
      try {
        if (props.handleSubmit) {
          result = await props.handleSubmit(props, getSchemas(props.pageFlowSteps, props.values));
        } else {
          const service = new KlantContactService(
            props.apiUrl,
            props.language,
            props.formulierKenmerk,
            props.onderwerp,
            props.schemaToRequestMapper
          );
          result = await service.klantContact(
            props.values,
            getSchemas(props.pageFlowSteps, props.values),
            props.requestInformation
          );
        }
        if (props.onSubmit) {
          props.onSubmit();
        }
        setklantContactResult(result);
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!klantContactResult) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <p>{error.message}</p>;
  }

  return (
    <>
      {klantContactResult &&
        (Array.isArray(klantContactResult) ? klantContactResult : [klantContactResult]).map(
          (result: ISubmitFormulierResult, index: number) => (
            <div key={index} className={"formulierpage-result"}>
              {props.thankYouPageContent}
              {result.formulierSamenvatting && (
                <DownloadComponent
                  base64String={result.formulierSamenvatting.base64Inhoud}
                  name={result.formulierSamenvatting.bestandsnaam}
                  downloadInformationContent={props.downloadInformationContent}
                />
              )}
            </div>
          )
        )}
      {props.overzichtPath && (
        <div className="formbutton-container">
          <a className="btn btn--primary" href={props.overzichtPath}>
            {translate("Klantondersteuning_Formulieren_NaarMijnCAKOverzicht")}
          </a>
        </div>
      )}
    </>
  );
};
